import { inject, observer } from 'mobx-react';
import { fetchTreeInsightsEnabled } from '../components/model/rgipChatApi';
import { legacyForced } from '../components/layout/legacy-mode';
import { LegacyExperienceToggle } from '../components/layout/legacy-experience-toggle';
import { TopbarSlot } from '../components/layout/topbar-slot';
import { AskAiButton } from '../components/layout/side-panel/AskAiButton';
import React from 'react';
import {
  Badge,
  Form,
  Nav,
  Button,
  Alert,
  ButtonGroup,
  Dropdown,
} from 'react-bootstrap';
import {
  Switch,
  Route,
  Redirect,
  NavLink,
  RouteComponentProps,
  withRouter,
} from 'react-router-dom';
import Store from '../../store';
import PageHeader from '../components/PageHeader';
import DuplicationModelModal from '../components/modals/DuplicationModelModal';
import DeployPage from './model/DeployPage';
import SimulationsPage from './model/SimulationsPage';
import UnivariateAnalysisPage from './model/UnivariateAnalysisPage';
import ModelDetailsPage from './model/ModelDetailsPage';
import ReviewPage from './model/ReviewPage';
import Overview from './model/Overview';
import FeatureEvaluationPage from './model/FeatureEvaluationPage';
import UnivariateLeadsPage from './model/UnivariateLeadsPage';
import { PROFILES_PERMISSIONS } from '../../permissions';
import checkPermissions, { hasRole } from '../components/CheckPermissions';
import AudiencePage from './model/buildDataset/AudiencePage';
import DataSetsTabs from '../components/audience/DataSetsTabs';
import MkAudiencePage from './model/buildDataset/build_dataset_pages/MkAudiencePage';
import MqaPage from './model/buildDataset/build_dataset_pages/MqaPage';
import RedshiftImportPage from './model/buildDataset/DatabaseImportPage';
import PqlPage from './model/buildDataset/build_dataset_pages/PqlPage';
import classNames from 'classnames';
import ModelStatus from '../components/ModelStatus';
import Undeploy from '../components/deploy/Undeploy';
import DeployModal from '../components/deploy/DeployModal';
import {
  emitAsk,
  handoffMessage,
  takeHandoff,
} from '../components/model/askBus';
import { ModelChatPanel } from '../components/model/ModelChatPanel';
import { recentCopies } from '../components/model/modelCopies';
import {
  onDatasetLoading,
  onModelRefresh,
} from '../components/model/modelChangeBus';
import { chatAgentFor } from '../components/model/rgipChatApi';
import './modelPage.css';
import queryString from 'querystring';
import { IS_LLTB, MODEL_TYPES_NAMES } from '../../utils/constants';

interface MatchParams {
  tenant: string;
  modelId: string;
}
interface ModelPageProps extends RouteComponentProps<MatchParams> {
  store?: Store;
}

interface ModelPageState {
  showDuplicatingModal: boolean;
  newModelName: string;
  showUndeployModal: boolean;
  /** The deploy dialog the Deployment tab was folded into. */
  showDeployModal: boolean;
  /**
   * Null while resolving. The revamp moved attribute insights under the tree
   * and dropped this link, but the `/insights` routes below still exist, so
   * without the link the page is reachable only by typed URL. A tenant the
   * revamp is off for keeps the way in they had.
   */
  revamped: boolean | null;
  /**
   * Whether this browser has opted out of the revamp.
   *
   * State, resolved after mount, NOT read from storage during render: the page
   * is server-rendered and then hydrated, and `localStorage` does not exist on
   * the server. Reading it in `render` made the first client render disagree
   * with the server markup, which React 17 does not repair — it keeps the
   * server DOM, so the switch landed in the wrong place on any refresh that
   * followed an opt-out.
   */
  legacyChosen: boolean;
}
const ModelPage = inject('store')(
  observer(
    class ModelPage extends React.Component<ModelPageProps, ModelPageState> {
      constructor(props: ModelPageProps) {
        super(props);
        this.state = {
          showDuplicatingModal: false,
          newModelName: '',
          showUndeployModal: false,
          showDeployModal: false,
          revamped: null,
          legacyChosen: false,
        };
        this.handleBatchChange = this.handleBatchChange.bind(this);
        this.handleRealtimeChange = this.handleRealtimeChange.bind(this);
        this.openDuplicateModelModal = this.openDuplicateModelModal.bind(this);
        this.closeDuplicatingModal = this.closeDuplicatingModal.bind(this);
        this.toggleShowUndeployModal = this.toggleShowUndeployModal.bind(this);
      }

      componentDidMount() {
        const { isModelIdValid, tenant } = this.props.store;
        this.setState({
          legacyChosen: legacyForced(String(this.props.match.params.tenant)),
        });

        // A question handed over from the models list. Read once and keyed by model,
        // so it cannot replay on a different one or on a reload. `askBus` holds it
        // until the chat panel subscribes, which happens after that panel's own
        // feature flag resolves, so emitting here cannot be too early.
        //
        // `handoffMessage` decides whether there is a turn to open at all. Arriving
        // after pressing a button carries context but no question, and context alone
        // opens nothing: nobody has asked this assistant anything yet.
        const opening = handoffMessage(
          takeHandoff(this.props.match.params.modelId)
        );
        if (opening) emitAsk(opening);
        fetchTreeInsightsEnabled(String(tenant))
          .catch(() => false)
          .then((revamped) => this.setState({ revamped }));
        if (!isModelIdValid) {
          this.props.history.replace(`/tenant/${tenant}/404`);
        }
        // The agent switched the model's base or wrote its thresholds. Re-read the
        // model, so a switched base reaches the chat (which moves to the other agent)
        // and every tab reading `activeModel.base` agrees with it.
        this.unsubscribeModelRefresh = onModelRefresh(() => {
          this.props.store.refreshModel().catch(() => undefined);
        });
        // The agent started a dataset load: the same flag the dataset page sets for its
        // own loads (`automateBuildModel`). Sent to the overview, which is where the
        // loading screen and its polling live; the other tabs are disabled meanwhile.
        this.unsubscribeDatasetLoading = onDatasetLoading(() => {
          const { tenant: t, modelId: m } = this.props.match.params;
          this.props.store.rootPage.markDataSetLoading();
          this.props.history.push(`/tenant/${t}/models/${m}/overview`);
        });
      }

      componentWillUnmount() {
        this.unsubscribeModelRefresh?.();
        this.unsubscribeDatasetLoading?.();
      }

      unsubscribeModelRefresh?: () => void;

      unsubscribeDatasetLoading?: () => void;

      async handleBatchChange() {
        const { activeModel } = this.props.store;
        const { batch, realtime } = activeModel;
        await this.props.store.updateModelContext({ realtime, batch: !batch });
      }

      async handleRealtimeChange() {
        const { activeModel } = this.props.store;
        const { batch, realtime } = activeModel;
        await this.props.store.updateModelContext({
          batch,
          realtime: !realtime,
        });
      }

      closeDuplicatingModal() {
        this.setState({
          showDuplicatingModal: false,
          newModelName: '',
        });
      }

      openDuplicateModelModal() {
        this.setState({ showDuplicatingModal: true });
      }

      toggleShowUndeployModal() {
        this.setState({ showUndeployModal: !this.state.showUndeployModal });
      }

      render() {
        const { tenant, modelId } = this.props.match.params;
        const basePath = `/tenant/${tenant}/models/${modelId}`;
        const {
          activeModel,
          rootPage: {
            updatingContext,
            isDataSetLoadingProgress,
            duplicationStatus,
          },
          isAllowedToEdit,
          tenantModels,
          csvPage: {
            dataSets: { dataSetMetadata },
          },
        } = this.props.store;
        const { showDuplicatingModal } = this.state;
        const isModelReady = !!dataSetMetadata && !isDataSetLoadingProgress;
        const isDuplicating = duplicationStatus === 'in_progress';
        const chatAgent = chatAgentFor(activeModel?.type, activeModel?.base);
        /* Copies of THIS model that already exist, for the chat to offer.
           Only on a live model, which is the only page where the question comes
           up: a live model cannot be edited, so every change asked for there ends
           in the offer to copy it and work on the copy. Everywhere else this is
           undefined and nothing is computed, sent or paid for.
           No fetch: `tenantModels` is the whole model list, resolved server side in
           `renderView.tsx` and hydrated into the store, which is the same place
           OverviewLegacy reads it to name the model this one was duplicated from. */
        const modelCopies = activeModel?.live
          ? recentCopies(tenantModels, modelId)
          : undefined;
        const parsedUrlQuery = queryString.parse(this.props.location.search);
        const showDuplicationWarningMessage =
          parsedUrlQuery['?showDuplicationWarningMessage'];

        return (
          // The chat rail is fixed to the viewport (see ModelChatPanel), so it
          // holds an in-flow 360px spacer here that only reserves width; the page
          // scrolls normally beneath the fixed panel.
          <div className="d-flex align-items-start">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <PageHeader>
                {/* Two rows, and which one an element belongs to is decided by
                    whether it is about THIS model. The name, what kind it is and
                    whether it is live are one identity group; Duplicate and Deploy
                    are what can be done to it. The experience toggle is not in here
                    at all: it is a preference, not something about this model, so it
                    lives in the top bar.

                    It wraps. Before this the row could not, so the title was the only
                    thing able to give and a two-word model name broke across two lines
                    while the buttons beside it kept their full width. */}
                {/* The experience toggle, portalled into the top bar beside the user
                    menu, so it takes no room in the header or the tab bar.

                    On the old header only for somebody who opted out, as their way
                    back. `legacyForced` turns the flag's true into false inside
                    `fetchTreeInsightsEnabled`, so they have `revamped === false`. */}
                {activeModel.type === 'customer_fit' &&
                  (this.state.revamped === true ||
                    (this.state.revamped === false &&
                      this.state.legacyChosen)) && (
                    <TopbarSlot>
                      <LegacyExperienceToggle
                        tenantId={String(tenant)}
                        forced={this.state.legacyChosen}
                      />
                    </TopbarSlot>
                  )}
                <div
                  className={
                    this.state.revamped
                      ? 'model-head'
                      : 'd-flex align-items-center'
                  }
                >
                  <div
                    className={
                      this.state.revamped ? 'model-head-id' : 'model-head-flat'
                    }
                  >
                    {/* Up one level, on the title's own row rather than as a
                      breadcrumb band above it. This header is already followed
                      by the tab bar, so a third stacked horizontal strip would
                      cost more than the navigation is worth.

                      It exists because there was no way back to the models list
                      from inside a model except the sidebar, so people reached
                      for the rail's left-pointing "Back to Admin" and left Data
                      Studio entirely. */}
                    {this.state.revamped && (
                      <NavLink
                        className="model-up"
                        to={`/tenant/${tenant}/models`}
                      >
                        Models
                      </NavLink>
                    )}
                    {/* The name truncates on the revamp, so the whole of it has to be
                      readable somewhere. Off the revamp it wraps, and a tooltip
                      repeating text already fully on screen is noise, which is why
                      this is conditional rather than always on. */}
                    <h2
                      id="page-header-model-name"
                      className={
                        this.state.revamped ? 'model-title' : undefined
                      }
                      title={this.state.revamped ? activeModel.name : undefined}
                    >
                      {activeModel.name}
                    </h2>

                    {updatingContext && (
                      <i aria-hidden className="fas fa-spinner fa-spin" />
                    )}
                    <Badge
                      className={classNames({
                        'mx-4': !this.state.revamped,
                        'cfit-badge': activeModel.type === 'customer_fit',
                        'mqa-badge': activeModel.type === 'mqa',
                        'pql-badge': IS_LLTB(activeModel.type),
                      })}
                    >
                      {MODEL_TYPES_NAMES[activeModel.type]}
                    </Badge>
                    {/* Whether it is live belongs with what it is, not with the
                      buttons: it is a fact about the model and it was reading as a
                      third control between Duplicate and Deploy.

                      The tooltip is the sentence that used to sit in this row in
                      italics, two lines of it at a narrow width, saying what the
                      badge beside it already said. A caveat belongs on the thing it
                      is about. */}
                    {this.state.revamped && (
                      <span
                        className="model-head-status"
                        title={
                          isAllowedToEdit
                            ? undefined
                            : 'This model is live and cannot be edited. Duplicate it to make changes.'
                        }
                      >
                        <ModelStatus model={activeModel} />
                      </span>
                    )}
                  </div>
                  {!updatingContext && (
                    <div
                      className={classNames('d-flex align-items-center', {
                        'model-head-actions': this.state.revamped,
                        'ms-auto': !this.state.revamped,
                      })}
                    >
                      {/* Left of the divider beside Deploy, and outside the
                          ARCHITECT check: asking about a model is not an edit.
                          Renders nothing where the chat is off, or where it is
                          on the page itself (Overview); Deploy is anchored at
                          the right edge either way, so it never moves. */}
                      {this.state.revamped && <AskAiButton />}
                      {/* `hasRole`, not `checkPermissions`: that wrapper declares
                          a new component class on every call, so each render of
                          this page remounted everything below. An open Deploy
                          menu closed itself, and Undeploy lost its in-flight
                          state, so the reload after it never fired (RGI-2981,
                          the same fix as the deploy dialog's). */}
                      {hasRole(
                        this.props.store,
                        PROFILES_PERMISSIONS.ARCHITECT
                      ) && (
                        <>
                          {/* On the revamp these two live in the Deploy menu. */}
                          {isAllowedToEdit || this.state.revamped || (
                            <>
                              {/* Off the revamp this sentence stays exactly where it
                                  was; on it, the status chip's tooltip carries it. */}
                              {!this.state.revamped && (
                                <span className="fst-italic me-1 text-muted me-4">
                                  This model is live and cannot be edited
                                </span>
                              )}

                              <Button
                                variant="outline-info me-2"
                                size="sm"
                                onClick={this.openDuplicateModelModal}
                              >
                                <i aria-hidden className="far fa-copy"></i>{' '}
                                Duplicate
                              </Button>

                              {activeModel.is_primary || (
                                <Undeploy
                                  tenant={Number(tenant)}
                                  model={activeModel}
                                />
                              )}
                            </>
                          )}
                          {!this.state.revamped && (
                            <ModelStatus model={activeModel} />
                          )}
                          {/* Batch and Realtime moved into the deploy dialog.
                              They sat here next to the model name and read as
                              a view setting, and they are deploy inputs: they
                              decide which config folders a deploy writes,
                              which is why the Deployment page said "No context
                              is selected" when both were off. Off the revamp
                              they stay exactly where they were. */}
                          {this.state.revamped === false && (
                            <>
                              {activeModel.type === 'customer_fit' && (
                                <Form.Check
                                  className="ms-2"
                                  type="checkbox"
                                  id="realtime"
                                  label="Realtime"
                                  inline
                                  checked={activeModel.realtime}
                                  disabled={!isAllowedToEdit}
                                  onChange={this.handleRealtimeChange}
                                />
                              )}
                              <Form.Check
                                className="ms-2"
                                type="checkbox"
                                label="Batch"
                                id="batch"
                                inline
                                checked={activeModel.batch}
                                disabled={!isAllowedToEdit}
                                onChange={this.handleBatchChange}
                              />
                            </>
                          )}
                          {/* Deploying is the last thing done to a model, so
                              the tab that held it was a destination passed
                              through once. As a button it is reachable from
                              whichever page the work finished on.

                              Behind Bootstrap's own rule, because the pill to
                              its left is a status and this is an action: at
                              the same height and a few pixels apart they read
                              as two halves of one control. */}
                          {this.state.revamped && (
                            <>
                              <div className="vr mx-3" />
                              {/* One control, one width, on every model: the
                                  row's right edge. Duplicate and Undeploy are in
                                  its menu rather than beside it, so a live model
                                  adds nothing to the row and nothing moves.

                                  A draft splits it: the face opens the deploy
                                  dialog, the caret the menu. A live model cannot
                                  be deployed again (the dialog blocks it and says
                                  to undeploy or duplicate), so the whole button
                                  says Deployed and opens the menu. Undeploy is
                                  offered only where it was before: a live model
                                  that is not the primary. */}
                              {activeModel.live ? (
                                <Dropdown className="model-deploy-btn">
                                  <Dropdown.Toggle
                                    variant="outline-primary"
                                    size="sm"
                                    title={`Live as ${
                                      activeModel.is_primary
                                        ? 'the Primary'
                                        : 'a Secondary'
                                    }${
                                      activeModel.type === 'customer_fit'
                                        ? ' Fit'
                                        : ''
                                    } model`}
                                  >
                                    Deployed
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      onClick={this.openDuplicateModelModal}
                                    >
                                      <i aria-hidden className="far fa-copy" />{' '}
                                      Duplicate
                                    </Dropdown.Item>
                                    {activeModel.is_primary || (
                                      <Dropdown.Item
                                        onClick={this.toggleShowUndeployModal}
                                      >
                                        <i
                                          aria-hidden
                                          className="fas fa-undo"
                                        />{' '}
                                        Undeploy
                                      </Dropdown.Item>
                                    )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              ) : (
                                <Dropdown
                                  as={ButtonGroup}
                                  className="model-deploy-btn"
                                >
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() =>
                                      this.setState({ showDeployModal: true })
                                    }
                                  >
                                    Deploy
                                  </Button>
                                  <Dropdown.Toggle
                                    split
                                    variant="primary"
                                    size="sm"
                                    aria-label="More actions"
                                  />
                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      onClick={this.openDuplicateModelModal}
                                    >
                                      <i aria-hidden className="far fa-copy" />{' '}
                                      Duplicate
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              )}
                              {activeModel.live && !activeModel.is_primary && (
                                <Undeploy
                                  tenant={Number(tenant)}
                                  model={activeModel}
                                  show={this.state.showUndeployModal}
                                  onHide={this.toggleShowUndeployModal}
                                />
                              )}
                            </>
                          )}
                        </>
                      )}
                      {/* Off the revamp there is no Deploy here, so it goes last,
                          at the row's right edge. */}
                      {this.state.revamped === false && (
                        <AskAiButton className="ms-2" />
                      )}
                    </div>
                  )}
                </div>
                <div>
                  {showDuplicationWarningMessage === 'true' && (
                    <Alert variant="warning">
                      If you plan to create new computations for this model,
                      please rebuild a dataset{' '}
                      <a href={`${basePath}/import_data/mk_audience`}>here</a>{' '}
                      before.
                    </Alert>
                  )}
                </div>
              </PageHeader>
              <Nav className="second-level-nav bg-white">
                <Nav.Link
                  className="text-dark"
                  as={NavLink}
                  to={`${basePath}/overview`}
                >
                  Overview
                </Nav.Link>
                {/* Dropped by the revamp, which put attribute insights under
                    the tree instead. The routes are still mounted below, so off
                    the revamp this is the only way to reach them. */}
                {this.state.revamped === false && (
                  <Nav.Link
                    className={classNames({ 'text-dark': isModelReady })}
                    as={NavLink}
                    to={`${basePath}/insights`}
                    disabled={!isModelReady}
                  >
                    Insights
                  </Nav.Link>
                )}
                <Nav.Link
                  className={classNames({ 'text-dark': isModelReady })}
                  as={NavLink}
                  to={`${basePath}/model`}
                  activeClassName="active-parent"
                  disabled={!isModelReady}
                >
                  Model
                </Nav.Link>
                <Nav.Link
                  className={classNames({ 'text-dark': isModelReady })}
                  as={NavLink}
                  to={`${basePath}/review`}
                  activeClassName="active-parent"
                  disabled={!isModelReady}
                >
                  Review
                </Nav.Link>
                {/* Folded into the header's Deploy button. The route below
                    stays mounted for bookmarks and for the tenants the revamp
                    is off for. */}
                {this.state.revamped === false && (
                  <Nav.Link
                    className={classNames({ 'text-dark': isModelReady })}
                    as={NavLink}
                    to={`${basePath}/deployment`}
                    disabled={!isModelReady}
                  >
                    Deployment
                  </Nav.Link>
                )}
                {/* Impact-simulation results — internal-only for now */}
                {checkPermissions(
                  PROFILES_PERMISSIONS.SUPERKUDU,
                  <Nav.Link
                    className="text-dark"
                    as={NavLink}
                    to={`${basePath}/simulations`}
                  >
                    Simulations
                  </Nav.Link>
                )}
              </Nav>
              <Switch>
                <Route path={`${this.props.match.path}/overview`}>
                  <Overview />
                </Route>
                <Route
                  path={`${this.props.match.path}/insights/:variableId/:variableName/:value/:readableValue/trees/:treeId/nodes/:nodeId/leads`}
                >
                  <UnivariateLeadsPage />
                </Route>
                <Route
                  path={`${this.props.match.path}/insights/:variableId/:variableName/:value/:readableValue/leads`}
                >
                  <UnivariateLeadsPage />
                </Route>
                <Route path={`${this.props.match.path}/insights`}>
                  {activeModel.type === 'customer_fit' ? (
                    <UnivariateAnalysisPage />
                  ) : (
                    <FeatureEvaluationPage />
                  )}
                </Route>
                <Route path={`${this.props.match.path}/model`}>
                  <ModelDetailsPage />
                </Route>
                <Route path={`${this.props.match.path}/review`}>
                  <ReviewPage
                    isCustomerFit={activeModel.type === 'customer_fit'}
                  />
                </Route>
                {/* Kept as a redirect rather than removed, the same way the
                    merged Review page keeps /sample. The path is bookmarked
                    and linked from elsewhere; on the revamp it lands on
                    Overview, where the header carries the Deploy button. */}
                <Route path={`${this.props.match.path}/deployment`}>
                  {this.state.revamped ? (
                    <Redirect to={`${basePath}/overview`} />
                  ) : (
                    <DeployPage />
                  )}
                </Route>
                <Route path={`${this.props.match.path}/simulations`}>
                  <SimulationsPage />
                </Route>
                <Route path={`${this.props.match.path}/import_data/mqa`}>
                  <MqaPage />
                </Route>
                <Route path={`${this.props.match.path}/import_data/pql`}>
                  <PqlPage />
                </Route>
                <Route path={`${this.props.match.path}/import_data/mql`}>
                  <PqlPage />
                </Route>
                <Route path={`${this.props.match.path}/import_data/pql2`}>
                  <PqlPage />
                </Route>
                <Route
                  path={`${this.props.match.path}/import_data/mk_audience`}
                >
                  <MkAudiencePage />
                </Route>
                <Route path={`${this.props.match.path}/import_data/database`}>
                  <RedshiftImportPage />
                </Route>
                <Route path={`${this.props.match.path}/import_data/audience`}>
                  <AudiencePage />
                </Route>
                <Route path={`${this.props.match.path}/import_data`}>
                  <DataSetsTabs />
                </Route>
                <Redirect
                  from={this.props.match.path}
                  to={`${this.props.match.path}/overview`}
                />
              </Switch>
              <DuplicationModelModal
                fromModel={Number(modelId)}
                close={this.closeDuplicatingModal}
                isShown={showDuplicatingModal}
              />
              {/* Mounted for the whole model rather than per page, the same
                  way the chat rail is: the button that opens it is in the
                  header, which every tab renders under.

                  Gated here rather than through `checkPermissions`, which
                  declares a new component class on every call: each render of
                  this page would remount the modal, closing Advanced and
                  replaying the open animation (RGI-2981). Same rule, through
                  `hasRole`. */}
              {this.state.revamped &&
                hasRole(this.props.store, PROFILES_PERMISSIONS.ARCHITECT) && (
                  <DeployModal
                    show={this.state.showDeployModal}
                    onHide={() => this.setState({ showDeployModal: false })}
                  />
                )}
            </div>
            {/* Persistent chat rail: one surface for the whole model, on every
                tab. Mounted only for a model type an agent can work on, which
                today is Customer Fit, then self-gates on the AI-insights flag
                (renders null when off). A node jump routes to the tree the same
                way Overview does. */}
            {chatAgent && (
              <ModelChatPanel
                tenantId={tenant}
                modelId={modelId}
                agent={chatAgent}
                /* Drives one of the opening questions. `live` is the app's own
                 notion of deployed (same field OverviewEnhanced reads), and
                 activeModel is null while the page loads, which reads as not
                 live and is the right way round: the question appears once we
                 know, rather than flashing on every page load. */
                isLive={!!activeModel?.live}
                /* The same readiness the tabs above are disabled on. A model whose
                 dataset is still building cannot be worked on at all, so the chat
                 must not offer a button for it either: the page already says no,
                 and an offer that contradicts the page is the panel's mistake. */
                isReady={isModelReady}
                /* The other wait, and the nearer one. `isReady` is about the dataset,
                 which takes hours; a model that is a copy still being assembled is held
                 by a job that takes minutes, and Studio's own card on this page says
                 so. Held back under the dataset caption, a fresh copy told people to
                 come back when the dataset finished building, which is the wrong wait
                 by two orders of magnitude on the one page where somebody is sitting
                 there waiting for it. */
                isDuplicating={isDuplicating}
                /* Named so the first reply can offer them, which is the only reply that
                 can: on a live model the agent answers from what it holds rather than
                 attempting a write, and its own lookup for these lives inside that
                 write. */
                modelCopies={modelCopies}
                /* Where a change the agent just made lives. `thresholds` and `signals`
                 redirect to Fine tuning and Review when the revamp is on, so one
                 target works on either layout. */
                onFocusPage={(page) =>
                  this.props.history.push(
                    `/tenant/${tenant}/models/${modelId}/model/${page}`
                  )
                }
                onFocusNode={(treeId, nodeId) =>
                  this.props.history.push(
                    `/tenant/${tenant}/models/${modelId}/model/trees/${treeId}/nodes/${nodeId}`
                  )
                }
                /* The agent duplicates a live model and this is the route to the
                 copy.

                 A full page load, not `history.push`. `activeModel` is resolved
                 server side (`renderView.tsx`) and stamped into the store at SSR
                 time, and the only client-side refresh re-fetches
                 `activeModel.modelId`, its own id, so it can never move to a
                 different model. A client-side push therefore changes the URL
                 and leaves the whole page rendering the model it was already on,
                 while the chat panel below takes `modelId` from the route and
                 re-keys itself to the copy: the page and the panel end up on
                 different models. Every other route into a model page in this
                 app is a full load for the same reason, including Studio's own
                 duplicate button (`DuplicationModelModal`). */
                onFocusModel={(newModelId) =>
                  window.location.assign(
                    `/tenant/${tenant}/models/${newModelId}`
                  )
                }
                onTrack={(eventName, properties) =>
                  this.props.store.analyticsTrack(eventName, {
                    ...properties,
                    model_type: activeModel?.type,
                  })
                }
              />
            )}
          </div>
        );
      }
    }
  )
);
export default withRouter(ModelPage);
